// Canvas header row — Figma "Frame 2147238866" (node 6384:18111).
//
// The editable report title on the left (DS CanvasSpaceTitle: click to rename,
// chevron opens a TwoPaneMenu of report templates) + Schedule / Customize
// actions on the right. Customize toggles the page's layout edit mode (shared
// report-layout store); Schedule is a placeholder action for the preview.
import {
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type CSSProperties,
	type ReactNode
} from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import {
	AppButton,
	AppSplitButton,
	CanvasSpaceTitle,
	Input,
	ListItem,
	MenuList,
	type TwoPaneMenuCategory,
	type TwoPaneMenuItem
} from '@clickguard/design-system'
import { AnimatePresence, motion } from 'framer-motion'
import {
	Check,
	Copy01,
	DotsHorizontal,
	Edit05,
	Lock01,
	Share04,
	Star01,
	Trash01,
	Users01
} from './icons'
import {
	useReportLayoutStore,
	isViewOwner,
	selectDirty,
	type SavedView,
	type ViewScope
} from '../store/use-report-layout-store'
import { usePrefersReducedMotion } from './customize-reveal'
import { ShareReportDrawer } from './schedule-report-drawer'
import './customize.css'

// The Default views (§2.2) are real saved views from the backend (scope
// 'default', seeded by migration-132) — they load a full layout like any other
// view, but nobody can save into, rename or delete them. Until the views have
// hydrated (or if none are seeded) the pill shows this placeholder.
const PLACEHOLDER_ID = '__none__'
const PLACEHOLDER_TITLE = 'Analytics report'

const BORDER = 'rgb(var(--cg-border))' // DS border token — flips with the theme
const PANE_WIDTH = 188 // actions pane width (Figma 6716:219203)
// Max popover height. The card fixes to the Shared-views (tallest tab) height, but
// never grows past this — beyond it the tab list scrolls inside. Sized to show about
// 7 regular (single-line) rows or 5 shared (two-line, "Created by") rows: single-line
// row pitch is 36px, two-line 50px, and each pane has 8px top + 8px bottom padding, so
// 7·36 + 16 = 268 ≈ 5·50 + 16 = 266.
const MENU_MAX_H = 268

type MenuBox = { left: number; top: number; width: number; height: number }

export const CanvasHeader = () => {
	const reduced = usePrefersReducedMotion()
	// Customize is always on now; the actions swap on whether there are unsaved
	// edits (draft ≠ last save), not on an edit toggle.
	const dirty = useReportLayoutStore(selectDirty)
	const saveLayout = useReportLayoutStore((s) => s.saveLayout)
	const discardLayout = useReportLayoutStore((s) => s.discardLayout)
	const savedViews = useReportLayoutStore((s) => s.savedViews)
	const activeViewId = useReportLayoutStore((s) => s.activeViewId)
	const fallbackViewId = useReportLayoutStore((s) => s.fallbackViewId)
	const viewsHydrated = useReportLayoutStore((s) => s.viewsHydrated)
	const defaultViewId = useReportLayoutStore((s) => s.defaultViewId)
	const saveAsView = useReportLayoutStore((s) => s.saveAsView)
	const requestViewSwap = useReportLayoutStore((s) => s.requestViewSwap)
	const renameView = useReportLayoutStore((s) => s.renameView)
	const duplicateView = useReportLayoutStore((s) => s.duplicateView)
	const deleteView = useReportLayoutStore((s) => s.deleteView)
	const setDefaultViewId = useReportLayoutStore((s) => s.setDefaultViewId)
	const setViewScope = useReportLayoutStore((s) => s.setViewScope)

	// Currently-shown report: a saved view if one is active, else a built-in
	// template. `selectedId` drives the menu highlight; `title` the displayed name.
	const activeView = savedViews.find((v) => v.id === activeViewId) ?? null
	const [title, setTitle] = useState(activeView?.label ?? PLACEHOLDER_TITLE)
	const [selectedId, setSelectedId] = useState(activeView?.id ?? PLACEHOLDER_ID)
	const [shareOpen, setShareOpen] = useState(false)

	// Follow the store's active view when its id changes underneath us — chiefly
	// the optimistic→real id swap after saving a NEW view (saveAsView/createDefault
	// set a temp `v_<ts>` id, then reconcileViewId replaces it with the server id).
	// We only move the local highlight/title when it was still tracking the OLD
	// active id, so a manual template pick (selectedId set, activeViewId unchanged)
	// is never clobbered. Without this, a freshly-saved view is wrongly treated as
	// a template and its rename/highlight target a dead id.
	const prevActiveViewId = useRef(activeViewId)
	useEffect(() => {
		const prev = prevActiveViewId.current
		if (activeViewId === prev) return
		prevActiveViewId.current = activeViewId
		// "Tracking" = the highlight still sits on the old active view, or on the
		// template placeholder that stands in when nothing is active (so the initial
		// main-view load — null → id — moves it too).
		if (selectedId === (prev ?? PLACEHOLDER_ID)) {
			setSelectedId(activeViewId ?? PLACEHOLDER_ID)
			const next = savedViews.find((v) => v.id === activeViewId)
			setTitle(next ? next.label : PLACEHOLDER_TITLE)
		}
	}, [activeViewId, selectedId, savedViews])

	// Deep link: arriving with ?drawer=schedule (e.g. from the Scheduled Reports
	// page's "Manage" action) opens the schedule drawer straight away. It ONLY
	// opens it — what the drawer shows (Schedule vs Stop/Save) is decided by
	// whether the report actually has a schedule on record, so the same report
	// reads the same way however you got here.
	const [searchParams] = useSearchParams()
	useEffect(() => {
		if (searchParams.get('drawer') === 'schedule') setShareOpen(true)
	}, [searchParams])

	// Fixed popover height = the Shared-views tab height (the tallest), so switching
	// tabs never grows/shrinks the card; a tab with more rows scrolls inside instead
	// (both panes are already overflow-y:auto). Locked via the DS paneHeight prop
	// (0.18.0) — sets height AND max-height on both panes, no measurement needed.
	const sharedViewCount = savedViews.filter((v) => v.scope === 'shared').length
	const menuPaneH = Math.min(
		MENU_MAX_H,
		Math.max(160, sharedViewCount * 58 + 16)
	)

	// Size the selector pill to the LONGEST report name so it doesn't resize (and
	// the chevron doesn't jump) every time you switch reports. Measure every name in
	// a hidden sizer that mirrors the pill's type (14px/medium), take the widest, and
	// floor the label's min-width to it. The current (shorter) name left-aligns in
	// that reserved width; the chevron stays put.
	const allReportNames = useMemo(
		() => [PLACEHOLDER_TITLE, ...savedViews.map((v) => v.label)],
		[savedViews]
	)
	const sizerRef = useRef<HTMLSpanElement>(null)
	const [pillLabelW, setPillLabelW] = useState<number>()
	useLayoutEffect(() => {
		const el = sizerRef.current
		if (!el) return
		let max = 0
		for (const name of allReportNames) {
			el.textContent = name
			max = Math.max(max, el.offsetWidth)
		}
		setPillLabelW(Math.ceil(max) + 1) // +1 guards against sub-pixel truncation
	}, [allReportNames])

	// ── Per-view actions pane (Figma 6716:218507) ──────────────────────────────
	// The kebab on a saved-view row reveals a THIRD pane flush against the two-pane
	// menu — one unified card with three equal-height columns (templates | reports |
	// actions). DS TwoPaneMenu is two panes, so we draw the unified card ourselves:
	// a single white "underlay" supplies the card's border/shadow/rounding, the DS
	// menu and this actions pane render transparent on top of it with internal
	// dividers, and both are forced to the card's (max) height.
	const [trayViewId, setTrayViewId] = useState<string | null>(null)
	const [menuBox, setMenuBox] = useState<MenuBox | null>(null)
	const [paneH, setPaneH] = useState<number | null>(null)
	const [trayMode, setTrayMode] = useState<'menu' | 'rename' | 'delete'>('menu')
	const [trayDraft, setTrayDraft] = useState('')
	const trayRef = useRef<HTMLDivElement>(null)
	const titleAreaRef = useRef<HTMLDivElement>(null)
	// When a report's scope is toggled from the actions pane, this holds the
	// category label the chevron menu should jump to next — so the popover follows
	// the report into its new "Your views" / "Shared views" section. Cleared by the
	// sync effect once it has navigated there.
	const pendingNavLabel = useRef<string | null>(null)
	// Whether the open menu has already been revealed+animated this open. Kept as a
	// ref (not effect-local) so it survives the reveal effect re-running when
	// savedViews/selectedId change mid-open (e.g. deleting a view): those must NOT
	// replay the entrance animation. Re-armed to false only when the menu leaves the
	// DOM, so a genuine reopen animates again.
	const menuRevealed = useRef(false)
	const trayView = trayViewId
		? savedViews.find((v) => v.id === trayViewId) ?? null
		: null

	const closeTray = () => {
		setTrayViewId(null)
		setTrayMode('menu')
		setMenuBox(null)
		setPaneH(null)
	}

	const openTray = (viewId: string) => {
		const menuEl = document.querySelector('.pa-canvas-title .cg-top-full')
		if (!menuEl) return
		const m = menuEl.getBoundingClientRect()
		setMenuBox({ left: m.left, top: m.top, width: m.width, height: m.height })
		setTrayMode('menu')
		setTrayViewId(viewId)
	}

	// Size the unified card to its tallest column (the actions pane in practice),
	// then push that height onto the menu via a CSS var so its columns grow to
	// match and the internal dividers run full height.
	useLayoutEffect(() => {
		if (!trayViewId || !menuBox || !trayRef.current) return
		const h = Math.max(menuBox.height, trayRef.current.offsetHeight)
		if (h !== paneH) setPaneH(h)
	}, [trayViewId, menuBox, trayMode, paneH])

	// Outside-click / Escape close the actions pane. The pane stops its own
	// mousedown propagation, so clicks inside it don't reach here — and don't reach
	// CanvasSpaceTitle's dismiss, which keeps the two-pane menu open. Clicking a
	// kebab is ignored so it can re-target another row.
	useEffect(() => {
		if (!trayViewId) return
		const onDown = (e: MouseEvent) => {
			const t = e.target as HTMLElement
			if (trayRef.current?.contains(t)) return
			if (t.closest?.('[data-view-kebab]')) return
			// A click still INSIDE the open menu (switching category/row) closes just
			// the actions pane now. A click fully OUTSIDE dismisses the whole popover —
			// don't tear the tray down here; let the DS remove its menu and the
			// close-animation effect snapshot + fade all layers together (otherwise the
			// tray vanishes a frame before the menu — the close "stutter").
			if (t.closest?.('.pa-canvas-title .cg-top-full')) closeTray()
		}
		const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && closeTray()
		document.addEventListener('mousedown', onDown)
		document.addEventListener('keydown', onEsc)
		return () => {
			document.removeEventListener('mousedown', onDown)
			document.removeEventListener('keydown', onEsc)
		}
	}, [trayViewId])

	// Actions reuse the store. Rename/Delete drop into a sub-panel; the instant
	// toggles (default/scope) stay open so the row label updates.
	const startTrayRename = () => {
		if (!trayView || !isViewOwner(trayView)) return
		setTrayDraft(trayView.label)
		setTrayMode('rename')
	}
	const commitTrayRename = () => {
		const name = trayDraft.trim()
		if (!name || !trayView) return
		renameView(trayView.id, name)
		if (selectedId === trayView.id) setTitle(name)
		closeTray()
	}
	const doTrayDuplicate = () => {
		if (!trayView) return
		const newId = duplicateView(trayView.id)
		setSelectedId(newId)
		setTitle(`${trayView.label} copy`)
		closeTray()
	}
	const doTrayDefault = () => {
		if (!trayView) return
		setDefaultViewId(defaultViewId === trayView.id ? null : trayView.id)
	}
	const doTrayScope = () => {
		if (!trayView || !isViewOwner(trayView)) return
		const next: ViewScope = trayView.scope === 'yours' ? 'shared' : 'yours'
		setViewScope(trayView.id, next)
		// Make the popover follow the report: queue the chevron menu to jump to the
		// section it now lives in, then collapse the actions pane so that menu shows.
		pendingNavLabel.current = next === 'shared' ? 'Shared views' : 'Your views'
		closeTray()
	}
	const doTrayDelete = () => {
		if (!trayView || !isViewOwner(trayView)) return
		deleteView(trayView.id)
		if (selectedId === trayView.id) {
			// The store drops the report onto the fallback Default view (§2.10).
			const fallback = savedViews.find((v) => v.id === fallbackViewId)
			setSelectedId(fallback?.id ?? PLACEHOLDER_ID)
			setTitle(fallback?.label ?? PLACEHOLDER_TITLE)
		}
		closeTray()
	}

	// A menu row label: name + a purple check when it's the current report + an
	// optional ••• that opens the actions pane. The kebab is the rightmost item; the
	// check sits just left of it in a fixed-width slot (always rendered, icon only
	// when selected) so the kebab stays pinned to the right edge and aligned across
	// rows whether or not the row is selected. We render the check ourselves (and pass
	// no selectedTemplateId to the DS menu) so it isn't duplicated in the trailing slot.
	const rowLabel = (
		id: string,
		name: string,
		withKebab: boolean,
		supporting?: ReactNode
	): ReactNode => {
		const isSel = id === selectedId
		return (
			<span className="flex w-full min-w-0 items-center gap-2">
				{/* Name + optional author line. The supporting line reuses the DS
				    ListItem supportingText classes (cg-text-text-xs / -secondary) so the
				    two-line row matches the canonical "List Item" variant exactly. */}
				<span className="flex min-w-0 flex-1 flex-col">
					<span className="truncate">{name}</span>
					{supporting && (
						<span className="cg-truncate cg-text-text-xs cg-text-text-secondary">
							{supporting}
						</span>
					)}
				</span>
				<span className="flex h-4 w-4 shrink-0 items-center justify-center">
					{isSel && <Check className="h-4 w-4 text-[rgb(var(--cg-brand))]" />}
				</span>
				{withKebab && (
					<span
						role="button"
						tabIndex={-1}
						data-view-kebab
						data-tray-active={trayViewId === id ? 'true' : undefined}
						aria-label={`Manage ${name}`}
						className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-gray-400 transition hover:bg-[rgba(0,0,0,0.10)] hover:text-gray-700 active:scale-90"
						onClick={(e) => {
							e.stopPropagation()
							openTray(id)
						}}
					>
						<DotsHorizontal className="h-4 w-4" />
					</span>
				)}
			</span>
		)
	}
	// Shared views show who created them under the name; private ("yours") views are
	// always you, so the line would be redundant and is omitted.
	const viewRow = (v: SavedView): TwoPaneMenuItem => {
		const supporting =
			v.scope === 'shared'
				? `Created by ${isViewOwner(v) ? 'you' : v.authorName ?? v.author}`
				: undefined
		return {
			id: v.id,
			searchText: v.label,
			label: rowLabel(v.id, v.label, true, supporting)
		}
	}

	// Title chevron menu: ClickGuard's Default views + the user's own & shared saved
	// views. Section order is FIXED — Default views, then Your views, then Shared
	// views — regardless of which report is active, so the panes never reshuffle on
	// selection. Default rows carry no ••• (§2.2: not editable, renamable or
	// deletable); the backend orders them by sort_order.
	const defaults = savedViews.filter((v) => v.scope === 'default')
	const yours = savedViews.filter((v) => v.scope === 'yours')
	const shared = savedViews.filter((v) => v.scope === 'shared')
	const templates: TwoPaneMenuCategory[] = [
		{
			id: 'default',
			label: 'Default views',
			items: defaults.map((d) => ({
				id: d.id,
				searchText: d.label,
				label: rowLabel(d.id, d.label, false)
			}))
		},
		...(yours.length
			? [{ id: 'yours', label: 'Your views', items: yours.map(viewRow) }]
			: []),
		...(shared.length
			? [{ id: 'shared', label: 'Shared views', items: shared.map(viewRow) }]
			: [])
	]

	// Open the menu on the category that holds the active report — WITHOUT
	// reordering the sections. CanvasSpaceTitle remounts its TwoPaneMenu on every
	// open with the active category reset to the first (Default views), and
	// exposes no prop to override that. So we nudge the left pane once per open:
	// when the menu mounts, click the category row whose label matches the
	// selected report's scope. A Default template is already the first category,
	// so nothing to do there.
	//
	// The click flips the active category via a React state update, which paints one
	// frame later — so a naive nudge shows Default highlighted for a frame then
	// "snaps" across. We kill that flash purely in CSS: `.cg-top-full` is
	// visibility:hidden until it carries the `.pa-menu-in` class (see customize.css).
	// The class is added only once the target category is actually selected, so the
	// very first PAINTED frame is already on the right category — and because the
	// gate is a standing CSS rule (not an inline style we set after mount), it holds
	// even if the DS re-renders the menu node between mount and reveal, which is what
	// let the flash slip through before. `.pa-menu-in` also drives the entrance
	// animation, so reveal and animate happen atomically. A Default template needs no
	// nudge, so it reveals immediately.
	useEffect(() => {
		const root = titleAreaRef.current
		if (!root) return
		const active = savedViews.find((v) => v.id === selectedId)
		// A pending scope-toggle override wins (jump the menu to the report's NEW
		// section); otherwise open on the active report's own section.
		const wantLabel =
			pendingNavLabel.current ??
			(active
				? active.scope === 'default'
					? null // Default views is already the first category
					: active.scope === 'shared'
					? 'Shared views'
					: 'Your views'
				: null)
		const show = (menu: HTMLElement) => {
			menu.classList.remove('pa-menu-in')
			void menu.offsetWidth // force reflow so the animation restarts on a reopen
			menu.classList.add('pa-menu-in')
		}
		const sync = () => {
			const menu = root.querySelector<HTMLElement>('.cg-top-full')
			if (!menu) {
				menuRevealed.current = false // menu closed — re-arm for the next open
				return
			}
			// Already revealed this open — a mid-open data change (savedViews/selectedId,
			// e.g. deleting a view) must NOT replay the entrance animation.
			if (menuRevealed.current) return
			// No active saved view → Default views is already first; reveal + animate now.
			if (!wantLabel) {
				menuRevealed.current = true
				show(menu)
				return
			}
			const cats = menu.querySelector(
				'[role="listbox"][aria-label="Categories"]'
			)
			if (!cats) return // panes not rendered yet — the CSS gate keeps it hidden
			const target = Array.from(
				cats.querySelectorAll<HTMLElement>('[role="option"]')
			).find((o) => o.textContent?.trim().startsWith(wantLabel))
			if (!target) return
			menuRevealed.current = true
			pendingNavLabel.current = null // consumed — future opens follow the active report again
			if (target.getAttribute('aria-selected') === 'true') {
				show(menu)
				return
			}
			target.click()
			let frames = 0
			const reveal = () => {
				if (target.getAttribute('aria-selected') === 'true' || frames++ > 20) {
					show(menu)
					return
				}
				requestAnimationFrame(reveal)
			}
			requestAnimationFrame(reveal)
		}
		const obs = new MutationObserver(sync)
		obs.observe(root, { childList: true, subtree: true })
		sync()
		return () => obs.disconnect()
	}, [savedViews, selectedId])

	// Close animation. The DS renders the popover as a bare `L && a && <div…>`
	// conditional (DS bundle ~7067) — it's removed from the DOM instantly with no
	// exit, and exposes no open/onOpenChange prop to defer that. So we animate the
	// close ourselves: a MutationObserver catches the `.cg-top-full` node leaving,
	// re-attaches that exact (now-detached) node inside a fixed ghost wrapper, and
	// snapshots our own actions tray + underlay alongside it, then fades the whole
	// stack out together (.pa-menu-out / .pa-tray-out). Re-using the real node, and
	// fading all layers as one, is what turns the layered instant teardown (the
	// "tuk-tuk-tuk") into a single clean dismiss. Ghosts live in <body>, never inside
	// a React-owned node, so they can't corrupt React's reconciliation.
	useEffect(() => {
		const root = titleAreaRef.current
		if (!root) return
		const reduce = () =>
			window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
		// The removed node is already detached when the observer reports it, so its
		// getBoundingClientRect() reads 0 — we cache the live menu's rect on every
		// mutation while it's open and use the last good one to place the ghost.
		let lastRect: DOMRect | null = null
		const ghost = (menuNode: HTMLElement) => {
			closeTray() // clear tray state so the live tray/underlay unmount
			const r = lastRect
			if (reduce() || !r || !r.width) return
			const trayEls = document.querySelectorAll<HTMLElement>(
				'.pa-tray-pane, .pa-tray-underlay'
			)
			const trayWasOpen = trayEls.length > 0
			const wrap = document.createElement('div')
			// Mirror the live context: when the tray was open the menu card's height comes
			// from `.pa-tray-open … min-height: var(--pa-pane-h)`. The ghost must carry both
			// or the card collapses to its short natural height the instant it detaches —
			// the "jump" before it fades. Reproduce them exactly so the ghost is pixel-identical.
			wrap.className =
				'pa-canvas-title pa-closing-ghost' +
				(trayWasOpen ? ' pa-tray-open' : '')
			wrap.style.cssText =
				'position:fixed;left:0;top:0;width:0;height:0;z-index:55;pointer-events:none'
			menuNode.style.cssText = `position:fixed;left:${r.left}px;top:${r.top}px;width:${r.width}px;margin:0`
			menuNode.classList.remove('pa-menu-in')
			menuNode.classList.add('pa-menu-out')
			// Pin the card to its exact last-rendered height so it can't snap shorter
			// (the tray-open min-height comes from a CSS var that won't follow the
			// detached node — inline !important is the reliable equivalent).
			if (trayWasOpen) {
				const card = menuNode.querySelector<HTMLElement>('.cg-overflow-hidden')
				card?.style.setProperty('min-height', `${r.height}px`, 'important')
			}
			wrap.appendChild(menuNode)
			// Snapshot the actions pane + underlay (if open) so they fade in lockstep.
			trayEls.forEach((el) => {
				const c = el.cloneNode(true) as HTMLElement
				c.classList.remove('pa-tray-pane', 'pa-tray-underlay') // drop their entrance anims
				c.classList.add('pa-tray-out')
				wrap.appendChild(c)
			})
			document.body.appendChild(wrap)
			window.setTimeout(() => wrap.remove(), 220)
		}
		const obs = new MutationObserver((muts) => {
			const live = root.querySelector('.cg-top-full')
			if (live) {
				const r = live.getBoundingClientRect()
				if (r.width) lastRect = r
			}
			for (const mu of muts) {
				mu.removedNodes.forEach((n) => {
					if (n instanceof HTMLElement && n.classList.contains('cg-top-full'))
						ghost(n)
				})
			}
		})
		obs.observe(root, { childList: true, subtree: true })
		return () => obs.disconnect()
		// eslint-disable-next-line
	}, [])

	// Anchor the popover to the title AND lift it above the page chrome. The DS renders
	// the menu position:absolute INSIDE the report's overflow-y-auto scroll container,
	// while the top toolbar is a sticky (z-20) sibling OUTSIDE it. So as the menu scrolls
	// up with the title it gets CLIPPED by the scroll container and vanishes behind the
	// toolbar — not a z-fight (the menu is z-50; bumping it does nothing), a clip. The fix
	// is position:fixed: it escapes the overflow clip (no transformed ancestors here) and
	// stacks at the root above the toolbar. We re-anchor the menu as fixed, placed right
	// under the title, recomputed every scroll so it tracks the title — and drag the fixed
	// tray along with it so the whole card stays together.
	useEffect(() => {
		const root = titleAreaRef.current
		if (!root) return
		const place = () => {
			const menu = root.querySelector<HTMLElement>('.cg-top-full')
			if (!menu || !menu.parentElement) return
			const a = menu.parentElement.getBoundingClientRect() // .cg-group.cg-relative anchor
			menu.style.position = 'fixed'
			menu.style.left = `${a.left}px`
			menu.style.top = `${a.bottom + 4}px` // matches the DS's cg-mt-1 offset
			menu.style.marginTop = '0'
			// BELOW the sticky page header (z-10) and navbar (z-20): the popover
			// belongs to the scrolled content, so on scroll it slides UNDER the
			// chrome instead of riding over it (Esteban, 2026-07-02).
			menu.style.zIndex = '5'
		}
		const onScroll = () => {
			place()
			if (!trayRef.current) return // tray open → keep it pinned to the menu's live rect
			const menu = root.querySelector('.cg-top-full')
			if (!menu) return
			const m = menu.getBoundingClientRect()
			setMenuBox({ left: m.left, top: m.top, width: m.width, height: m.height })
		}
		// Place it as soon as the menu mounts, then keep it placed through scroll.
		const obs = new MutationObserver(place)
		obs.observe(root, { childList: true, subtree: true })
		// Capture phase so inner scroll containers (scroll events don't bubble) reach us.
		window.addEventListener('scroll', onScroll, true)
		return () => {
			obs.disconnect()
			window.removeEventListener('scroll', onScroll, true)
		}
		// eslint-disable-next-line
	}, [])

	// Re-animate the Items pane whenever the active CATEGORY switches (Default
	// views ↔ Your views ↔ Shared views). The DS swaps the items column's
	// contents in place with no transition, so we watch the categories' aria-selected
	// and retrigger the `.pa-items-in` slide-fade on the items listbox. The first
	// detection per open is skipped — the menu's own entrance already covers it (and
	// the open-time category nudge shouldn't read as a manual switch).
	useEffect(() => {
		const root = titleAreaRef.current
		if (!root) return
		let lastCat: string | null = null
		const obs = new MutationObserver(() => {
			const menu = root.querySelector<HTMLElement>('.cg-top-full')
			if (!menu) {
				lastCat = null // menu closed — re-arm
				return
			}
			const cats = menu.querySelector(
				'[role="listbox"][aria-label="Categories"]'
			)
			const items = menu.querySelector<HTMLElement>(
				'[role="listbox"][aria-label="Items"]'
			)
			if (!cats || !items) return
			const sel = cats.querySelector('[role="option"][aria-selected="true"]')
			const id = sel?.textContent?.trim() ?? null
			if (id === lastCat) return
			const first = lastCat === null
			lastCat = id
			if (first) return
			items.classList.remove('pa-items-in')
			void items.offsetWidth
			items.classList.add('pa-items-in')
		})
		obs.observe(root, {
			childList: true,
			subtree: true,
			attributes: true,
			attributeFilter: ['aria-selected']
		})
		return () => obs.disconnect()
	}, [])

	// Save split-button menu (Figma: Save · ⌄ → save-as options). When non-null,
	// `nameMode` swaps the menu for a "name this view" form scoped to yours/shared.
	const [saveOpen, setSaveOpen] = useState(false)
	const [nameMode, setNameMode] = useState<ViewScope | null>(null)
	const [nameDraft, setNameDraft] = useState('')
	const saveRef = useRef<HTMLDivElement>(null)
	// "Add new" (switcher) opens the SAME name form as Save-as, but anchored under
	// the report pill instead of the Save split button.
	const [addNewOpen, setAddNewOpen] = useState(false)
	const addNewRef = useRef<HTMLDivElement>(null)
	const addNewTimer = useRef<number | null>(null)

	const closeSaveMenu = () => {
		setSaveOpen(false)
		setNameMode(null)
	}

	const closeAddNew = () => {
		if (addNewTimer.current) window.clearTimeout(addNewTimer.current)
		setAddNewOpen(false)
		setNameMode(null)
	}

	useEffect(() => {
		if (!saveOpen) return
		const onDown = (e: MouseEvent) => {
			if (saveRef.current && !saveRef.current.contains(e.target as Node))
				closeSaveMenu()
		}
		const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && closeSaveMenu()
		document.addEventListener('mousedown', onDown)
		document.addEventListener('keydown', onEsc)
		return () => {
			document.removeEventListener('mousedown', onDown)
			document.removeEventListener('keydown', onEsc)
		}
	}, [saveOpen])

	useEffect(() => {
		if (!addNewOpen) return
		const onDown = (e: MouseEvent) => {
			if (addNewRef.current && !addNewRef.current.contains(e.target as Node))
				closeAddNew()
		}
		const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && closeAddNew()
		document.addEventListener('mousedown', onDown)
		document.addEventListener('keydown', onEsc)
		return () => {
			document.removeEventListener('mousedown', onDown)
			document.removeEventListener('keydown', onEsc)
		}
	}, [addNewOpen])

	// A Default view is read-only (§2.5: "Default views cannot be overwritten"), so
	// there's no view to save into — the only valid moves are Save as new / shared
	// view. Same when nothing is active yet (no defaults seeded).
	const selectedView = savedViews.find((v) => v.id === selectedId) ?? null
	const onTemplate = !selectedView || selectedView.scope === 'default'

	// "Save changes" commits the draft to the saved snapshot (and, if a saved view
	// is active, back into that view) and leaves Customize. But on a template there's
	// nothing to overwrite, so hitting Save (main button OR "Save changes") instead
	// opens the save-as menu — the only valid moves are Save as new / shared view.
	const handleSave = () => {
		if (onTemplate) {
			setSaveOpen(true)
			return
		}
		closeSaveMenu()
		saveLayout()
	}

	// "Save as new / shared view" — open the name form prefilled with the current
	// report name so one Enter creates the view.
	const openNameForm = (scope: ViewScope) => {
		setNameMode(scope)
		setNameDraft(title)
	}

	const commitNewView = () => {
		const name = nameDraft.trim()
		if (!name || !nameMode) return
		// Both "Add new" (switcher) and Save-as-new snapshot WHATEVER IS ON THE PAGE
		// right now — the current draft, adjustments included — under the new name
		// (§2.4: "a duplicate of whatever view is currently on the page"). Add new
		// used to reset to the default report, which lost the user's setup.
		const id = saveAsView(name, nameMode)
		setSelectedId(id)
		setTitle(name)
		setAddNewOpen(false)
		closeSaveMenu()
	}

	// The "name this view" card — a plain render helper (NOT a component) so it
	// doesn't remount the autoFocus Input on every keystroke. Reused by the Save
	// split-button popover and the switcher's "Add new" popover.
	const renderNameForm = (onCancel: () => void) => (
		<div className="flex flex-col gap-3 rounded-2xl border border-[rgb(var(--cg-border))] bg-white p-3 shadow-dropShadow">
			<Input
				size="sm"
				state="default"
				label={
					nameMode === 'shared' ? 'Save as shared view' : 'Save as new view'
				}
				value={nameDraft}
				autoFocus
				placeholder="View name…"
				onChange={(e) => setNameDraft(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === 'Enter') commitNewView()
				}}
			/>
			<div className="flex justify-end gap-2">
				<AppButton
					variant="ghost"
					size="sm"
					label="Cancel"
					onClick={onCancel}
				/>
				<AppButton
					variant="primary"
					size="sm"
					label="Save view"
					onClick={commitNewView}
					disabled={!nameDraft.trim()}
				/>
			</div>
		</div>
	)

	// "Add new" (portaled into the categories pane): dismiss the switcher and open
	// the "name this view" form (prefilled "New view") anchored under the pill, so
	// the view isn't created until the user names + confirms it. Escape reaches
	// CanvasSpaceTitle's own key handler, which owns the menu's open state.
	const openAddNewForm = () => {
		closeTray()
		document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))
		setNameMode('yours')
		setNameDraft('New view')
		// Let the DS menu finish its close-ghost fade (~130ms) before the card
		// enters, so the two surfaces hand off cleanly instead of overlapping
		// mid-transition (the card would otherwise fade in over the still-fading menu).
		if (addNewTimer.current) window.clearTimeout(addNewTimer.current)
		if (reduced) {
			setAddNewOpen(true)
			return
		}
		addNewTimer.current = window.setTimeout(() => setAddNewOpen(true), 150)
	}

	// Deep link: arriving with ?view=new (the Scheduled Reports page's "New
	// report" action) opens the same "name this view" form the switcher's "Add
	// new" row does. A new report starts life as a new analytics view; putting a
	// schedule on it is a later, separate step — which is why this does NOT open
	// the schedule drawer. The tray-close and Escape inside openAddNewForm are
	// no-ops on a fresh arrival (no menu is open yet).
	useEffect(() => {
		if (searchParams.get('view') === 'new') openAddNewForm()
		// openAddNewForm is stable enough for a mount-time deep link; re-running
		// it on every render-identity change would reopen the card under the user.
		// eslint-disable-next-line
	}, [searchParams])

	// Loading a saved view replaces the whole draft, so a dirty draft must be
	// resolved first (save or discard) — `applyView` itself has no guard, and a
	// silent switch used to destroy unsaved edits. `pendingView` holds the target
	// while the confirm dialog is up.
	const [pendingView, setPendingView] = useState<SavedView | null>(null)

	const goToView = (view: SavedView) => {
		setSelectedId(view.id)
		setTitle(view.label)
		// Hand the swap to the page's out→in transition (it defers the actual
		// applyView until the current report has dissolved out) instead of
		// swapping the layout synchronously, which snaps.
		requestViewSwap(view.id)
	}

	// Deep link: ?view=<saved view id> (the Scheduled Reports page's "Manage" adds
	// it next to ?drawer=schedule) loads that view, so the drawer opens on the
	// report the schedule belongs to. It waits for the BACKEND view list: the
	// persisted list can be another user's or workspace's, and acting on it early
	// showed the right view for a moment before the real load yanked the page to
	// the main view. The views sync loads the linked view itself, so this is only
	// the fallback for a link applied after hydration; applies once per id.
	const deepLinkedViewRef = useRef<string | null>(null)
	useEffect(() => {
		if (!viewsHydrated) return
		const id = searchParams.get('view')
		if (!id || id === 'new' || deepLinkedViewRef.current === id) return
		const view = savedViews.find((v) => v.id === id)
		if (!view) return
		deepLinkedViewRef.current = id
		if (activeViewId !== id) goToView(view)
		// goToView is recreated every render; the ref guards re-application.
		// eslint-disable-next-line
	}, [viewsHydrated, searchParams, savedViews, activeViewId])

	// Picking a report from the title menu: load a saved view's layout, or just
	// rename for a built-in template. Closing the menu also dismisses the pane.
	const selectReport = (item: TwoPaneMenuItem) => {
		closeTray()
		const view = savedViews.find((v) => v.id === item.id)
		if (view) {
			if (dirty && view.id !== selectedId) {
				setPendingView(view)
				return
			}
			goToView(view)
		}
	}

	// Renaming the report inline keeps the active saved view's label in sync so the
	// chevron menu always reflects the current report name.
	const renameReport = (next: string) => {
		setTitle(next)
		if (savedViews.some((v) => v.id === selectedId))
			renameView(selectedId, next)
	}

	const trayOpen = !!(trayView && menuBox)
	const trayIsDefault = trayView ? defaultViewId === trayView.id : false
	// Only the view's author may rename / delete / re-scope it. Non-owners still get
	// Duplicate + Set-as-default; the owner-only rows render disabled so the
	// permission is visible (and the card height stays the same either way).
	const trayIsOwner = trayView ? isViewOwner(trayView) : false
	const ownerOnly = (action: string) =>
		trayIsOwner ? undefined : `Only ${trayView?.authorName ?? trayView?.author} can ${action} this view`
	const cardHeight = paneH ?? menuBox?.height

	return (
		<div className="relative flex items-center gap-3">
			{/* "Add new" name form — the same card as the Save-as popover, anchored
			    under the report pill (switcher lives on the left). */}
			<AnimatePresence>
				{addNewOpen && (
					<motion.div
						ref={addNewRef}
						className="absolute left-0 top-full z-30 mt-1.5 w-64"
						style={{ transformOrigin: 'top left' }}
						initial={
							reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: -6 }
						}
						animate={
							reduced ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }
						}
						exit={
							reduced ? { opacity: 0 } : { opacity: 0, scale: 0.985, y: -4 }
						}
						transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
					>
						{renderNameForm(closeAddNew)}
					</motion.div>
				)}
			</AnimatePresence>
			<div
				ref={titleAreaRef}
				className={`pa-canvas-title pa-report-pill flex items-center${
					trayOpen ? ' pa-tray-open' : ''
				}`}
				style={
					{
						...(paneH ? { ['--pa-pane-h']: `${paneH}px` } : {}),
						...(pillLabelW ? { ['--pa-pill-label-w']: `${pillLabelW}px` } : {}),
						['--pa-menu-h']: `${menuPaneH}px`
					} as CSSProperties
				}
				onClick={(e) => {
					// The whole pill is the dropdown trigger: forward clicks (except on the
					// chevron itself or inside the open menu) to the chevron so it opens the
					// same templates popover.
					const t = e.target as HTMLElement
					if (
						t.closest('button[aria-haspopup="menu"]') ||
						t.closest('.cg-top-full')
					)
						return
					titleAreaRef.current
						?.querySelector<HTMLButtonElement>('button[aria-haspopup="menu"]')
						?.click()
				}}
			>
				<CanvasSpaceTitle
					value={title}
					onValueChange={renameReport}
					editable={false}
					templates={templates}
					onSelectTemplate={selectReport}
					menuProps={{
						// DS-native since 0.18.0 (folded back from this page): the
						// two-tone card and hairline ring + floating shadow (elevated)
						// replace the customize.css overrides; the "Add new" row rides
						// the categoriesFooter slot instead of a portal into the pane.
						// Height lock stays CSS-side (--pa-menu-h): the DS paneHeight
						// prop is ineffective on 0.18's flex-1 panes (bug, fix queued).
						twoTone: true,
						elevated: true,
						categoriesFooter: (
							// Padded to match the categories pane's own cg-p-2 (twoTone):
							// the footer is rendered OUTSIDE that padded scroll pane, so
							// without this the "Add new" pill sits flush to the pane edges
							// while the view rows above it are inset 8px.
							<div className="cg-px-2 cg-pb-2">
								{/* No top margin: the scroll pane above already contributes its
								    own 8px bottom padding, so the divider only needs matching
								    8px below to sit evenly between "Shared views" and "Add new". */}
								<div className="mb-2 mt-0 h-px bg-[rgb(var(--cg-border))]" />
								<ListItem
									label={
										<span className="font-medium text-[rgb(var(--cg-brand))]">Add new</span>
									}
									onClick={openAddNewForm}
								/>
							</div>
						)
					}}
				/>
				{/* Hidden sizer: mirrors the pill's type so we can measure the widest
				    report name and floor the label width to it (no resize on switch). */}
				<span
					ref={sizerRef}
					aria-hidden
					className="pa-pill-sizer cg-font-sans"
				/>
			</div>

			<div className="pa-header-actions ml-auto flex items-center gap-2">
				{/* At rest → a single Share action (opens the schedule drawer for now).
				    The moment the draft has unsaved edits it swaps to Discard + Save.
				    Opacity+y crossfade (mode="wait") so one group holds the slot at a
				    time and the row doesn't churn. */}
				<AnimatePresence mode="wait" initial={false}>
					{dirty ? (
						<motion.div
							key="editing"
							className="flex items-center gap-2"
							initial={reduced ? { opacity: 0 } : { opacity: 0, y: 2 }}
							animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
							exit={reduced ? { opacity: 0 } : { opacity: 0, y: -2 }}
							transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
						>
							{/* Discard reverts the draft (layout + table) to the last save. */}
							<AppButton
								variant="secondary"
								size="sm"
								className="pa-edit-btn"
								label="Discard"
								onClick={discardLayout}
							/>
							{/* Save split button: Main = save changes; chevron opens the
							    save-as menu (Figma split-button). */}
							<div ref={saveRef} className="relative">
								<AppSplitButton
									label="Save"
									size="sm"
									className="pa-save-split"
									onClick={handleSave}
									onMenuClick={() =>
										saveOpen ? closeSaveMenu() : setSaveOpen(true)
									}
									menuOpen={saveOpen}
									menuLabel="Save options"
								/>
								{/* Canonical popover entrance/exit (mirrors AnchoredPopover): fade +
						    3% scale + a small downward slide; right-aligned → origin 'top right'. */}
								<AnimatePresence>
									{saveOpen && (
										<motion.div
											className="absolute right-0 top-full z-30 mt-1.5 w-64"
											style={{ transformOrigin: 'top right' }}
											initial={
												reduced
													? { opacity: 0 }
													: { opacity: 0, scale: 0.97, y: -6 }
											}
											animate={
												reduced
													? { opacity: 1 }
													: { opacity: 1, scale: 1, y: 0 }
											}
											exit={
												reduced
													? { opacity: 0 }
													: { opacity: 0, scale: 0.985, y: -4 }
											}
											transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
										>
											{/* Swap between the options menu and the "name this view" form
									    with a light opacity + y fade, keyed on the sub-state. */}
											<AnimatePresence mode="wait" initial={false}>
												{nameMode ? (
													<motion.div
														key="name"
														initial={
															reduced ? { opacity: 0 } : { opacity: 0, y: 4 }
														}
														animate={
															reduced ? { opacity: 1 } : { opacity: 1, y: 0 }
														}
														exit={
															reduced ? { opacity: 0 } : { opacity: 0, y: -4 }
														}
														transition={{
															duration: 0.16,
															ease: [0.16, 1, 0.3, 1]
														}}
													>
														{renderNameForm(() => setNameMode(null))}
													</motion.div>
												) : (
													<motion.div
														key="menu"
														initial={
															reduced ? { opacity: 0 } : { opacity: 0, y: 4 }
														}
														animate={
															reduced ? { opacity: 1 } : { opacity: 1, y: 0 }
														}
														exit={
															reduced ? { opacity: 0 } : { opacity: 0, y: -4 }
														}
														transition={{
															duration: 0.16,
															ease: [0.16, 1, 0.3, 1]
														}}
													>
														<MenuList>
															{/* On a template there's nothing to "save changes" into —
													    only saving out to a new/shared view is valid. */}
															{!onTemplate && (
																<ListItem
																	label="Save changes"
																	onClick={handleSave}
																/>
															)}
															<ListItem
																label="Save as new view"
																onClick={() => openNameForm('yours')}
															/>
															<ListItem
																label="Save as shared view"
																onClick={() => openNameForm('shared')}
															/>
														</MenuList>
													</motion.div>
												)}
											</AnimatePresence>
										</motion.div>
									)}
								</AnimatePresence>
							</div>
						</motion.div>
					) : (
						<motion.div
							key="share"
							initial={reduced ? { opacity: 0 } : { opacity: 0, y: 2 }}
							animate={reduced ? { opacity: 1 } : { opacity: 1, y: 0 }}
							exit={reduced ? { opacity: 0 } : { opacity: 0, y: -2 }}
							transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
						>
							<AppButton
								variant="secondary"
								size="sm"
								label="Share"
								leftIcon={<Share04 className="h-4 w-4" />}
								onClick={() => setShareOpen(true)}
							/>
						</motion.div>
					)}
				</AnimatePresence>
			</div>

			{/* Unified 3-pane card: underlay (border/shadow/round) behind the DS menu
			    (left two panes, made transparent via .pa-tray-open) + this actions pane.
			    The Delete view row reads as a normal action at rest and turns destructive
			    (DS danger red) only on hover (.pa-delete-item in customize.css); a confirm
				    step still gates the actual delete. */}
			{trayOpen && menuBox && (
				<>
					<div
						aria-hidden
						className="pa-tray-underlay pointer-events-none fixed z-[3] rounded-xl bg-[rgb(var(--cg-canvas))]"
						style={{
							left: menuBox.left,
							top: menuBox.top,
							width: menuBox.width + PANE_WIDTH,
							height: cardHeight,
							boxShadow: `0 0 0 1px ${BORDER}, 0px 24px 48px -12px rgba(16, 24, 40, 0.18)`
						}}
					/>
					{/* z-[4]: between the underlay (3) and the DS menu (5) — the whole
					    tray cluster stays below the sticky header/navbar (10/20). */}
					<div
						ref={trayRef}
						className="pa-tray-pane fixed z-[4] flex flex-col"
						style={{
							left: menuBox.left + menuBox.width,
							top: menuBox.top,
							width: PANE_WIDTH,
							minHeight: paneH ?? undefined
						}}
						onMouseDown={(e) => e.stopPropagation()}
					>
						{trayMode === 'rename' ? (
							// Text/input pinned to the top, buttons to the bottom (justify-between
							// over the full card height) so the action buttons always sit in the
							// same place regardless of which sub-panel is showing. The heading
							// matches the menu list-item typography (14px/medium, text-primary)
							// rather than the DS Input's larger field label.
							<div
								className="pa-tray-content flex flex-col justify-between p-3"
								style={{ height: cardHeight }}
							>
								<div className="flex flex-col gap-2">
									{/* text-base = 14px in this app's scale (text-sm is 12px); font-normal
									    to match the menu list-item type exactly. */}
									<div className="text-base font-normal text-[rgb(var(--cg-text-primary))]">
										Rename view
									</div>
									<Input
										size="sm"
										state="default"
										aria-label="Rename view"
										value={trayDraft}
										autoFocus
										placeholder="View name…"
										onChange={(e) => setTrayDraft(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === 'Enter') commitTrayRename()
											else if (e.key === 'Escape') setTrayMode('menu')
										}}
									/>
								</div>
								<div className="flex justify-end gap-2">
									<AppButton
										variant="ghost"
										size="sm"
										label="Cancel"
										onClick={() => setTrayMode('menu')}
									/>
									<AppButton
										variant="primary"
										size="sm"
										label="Rename"
										onClick={commitTrayRename}
										disabled={!trayDraft.trim()}
									/>
								</div>
							</div>
						) : trayMode === 'delete' ? (
							<div
								className="pa-tray-content flex flex-col justify-between p-3"
								style={{ height: cardHeight }}
							>
								<div className="text-base font-normal text-[rgb(var(--cg-text-primary))]">
									Delete <span className="font-semibold">{trayView.label}</span>
									? This can't be undone.
								</div>
								<div className="flex justify-end gap-2">
									<AppButton
										variant="ghost"
										size="sm"
										label="Cancel"
										onClick={() => setTrayMode('menu')}
									/>
									<AppButton
										variant="primary"
										size="sm"
										label="Delete view"
										onClick={doTrayDelete}
									/>
								</div>
							</div>
						) : (
							<div className="pa-actions-list flex h-full flex-col p-2">
								<ListItem
									label="Rename"
									leadingIcon={<Edit05 className="h-4 w-4" />}
									disabled={!trayIsOwner}
									title={ownerOnly('rename')}
									onClick={startTrayRename}
								/>
								<ListItem
									label="Duplicate"
									leadingIcon={<Copy01 className="h-4 w-4" />}
									onClick={doTrayDuplicate}
								/>
								<ListItem
									label={
										trayIsDefault ? 'Remove as main view' : 'Set as main view'
									}
									leadingIcon={<Star01 className="h-4 w-4" />}
									selected={trayIsDefault}
									onClick={doTrayDefault}
								/>
								<ListItem
									label={
										trayView.scope === 'yours' ? 'Make shared' : 'Make private'
									}
									leadingIcon={
										trayView.scope === 'yours' ? (
											<Users01 className="h-4 w-4" />
										) : (
											<Lock01 className="h-4 w-4" />
										)
									}
									disabled={!trayIsOwner}
									title={ownerOnly('change the visibility of')}
									onClick={doTrayScope}
								/>
								<ListItem
									label="Delete view"
									leadingIcon={<Trash01 className="h-4 w-4" />}
									className="pa-delete-item"
									disabled={!trayIsOwner}
									title={ownerOnly('delete')}
									onClick={() => setTrayMode('delete')}
								/>
								<div className="mx-2 my-1 h-px bg-[rgb(var(--cg-border))]" />
								<ListItem
									label={
										<span className="font-medium text-[rgb(var(--cg-brand))]">Cancel</span>
									}
									onClick={closeTray}
								/>
							</div>
						)}
					</div>
				</>
			)}

			<ShareReportDrawer
				open={shareOpen}
				onClose={() => setShareOpen(false)}
				reportName={title}
			/>

			{/* Unsaved-changes guard: switching views replaces the whole draft, so a
			    dirty draft gets a Save / Discard / Cancel stop first. Portaled over
			    everything (same layer as the drawers); Cancel is the safe default
			    focus so Enter can't destroy work. On a template there's nothing to
			    save into, so the save option drops and the copy says why. */}
			{createPortal(
				<AnimatePresence>
					{pendingView && (
						<motion.div
							className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
							onMouseDown={(e) => {
								if (e.target === e.currentTarget) setPendingView(null)
							}}
							onKeyDown={(e) => e.key === 'Escape' && setPendingView(null)}
						>
							<motion.div
								role="dialog"
								aria-modal="true"
								aria-labelledby="pa-unsaved-title"
								className="w-[400px] rounded-2xl border border-[rgb(var(--cg-border))] bg-white p-4 shadow-dropShadow"
								initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: -6 }}
								animate={reduced ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
								exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.985, y: -4 }}
								transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
							>
								<div id="pa-unsaved-title" className="text-[14px] font-medium text-[rgb(var(--cg-text-primary))]">
									Unsaved changes
								</div>
								<p className="mt-1.5 text-[12px] leading-[1.5] text-[rgb(var(--cg-text-secondary))]">
									{`Switching to "${pendingView.label}" will discard the edits you haven't saved.`}
									{onTemplate &&
										` You’re on a default view, so saving keeps them as a new view named "${title} copy".`}
								</p>
								<div className="mt-4 flex items-center justify-between gap-2">
									<AppButton
										variant="ghost"
										size="sm"
										label="Cancel"
										autoFocus
										onClick={() => setPendingView(null)}
									/>
									<div className="flex items-center gap-2">
										<AppButton
											variant="destructive"
											size="sm"
											label="Discard and switch"
											onClick={() => {
												const target = pendingView
												setPendingView(null)
												discardLayout()
												goToView(target)
											}}
										/>
										<AppButton
											variant="primary"
											size="sm"
											label="Save and switch"
											onClick={() => {
												const target = pendingView
												setPendingView(null)
												// A Default view can't be overwritten: the edits become a new
												// private view auto-named "{name} copy" (§2.11), no prompt.
												if (onTemplate) saveAsView(`${title} copy`, 'yours')
												else saveLayout()
												goToView(target)
											}}
										/>
									</div>
								</div>
							</motion.div>
						</motion.div>
					)}
				</AnimatePresence>,
				document.body
			)}
		</div>
	)
}
